import type { TMessageKey } from '@/src/lang/useTranslate'

export class ContactFormValidator {
  constructor(private translator: (key: TMessageKey) => string) {}

  private isEmptyFirstname(firstname: string) {
    return firstname.trim().length === 0
  }

  private isTooLongFirstname(firstname: string) {
    return firstname.trim().length > 30
  }

  private isEmptyMessage(message: string) {
    return message.trim().length === 0
  }

  private isTooShortMessage(message: string) {
    return message.trim().length < 100
  }

  isValidCaptcha(captcha: string) {
    return captcha.trim().length > 0
  }

  isValidEmail(email: string) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  isValidLastname(lastname: string) {
    return lastname.trim().length > 0
  }

  isValidSubject(subject: string) {
    return subject.trim().length > 0
  }

  isValidForm(form: TContactForm) {
    const isAtLeastOneError = Object.values(form).some(({ error }) => error?.trim())
    return !isAtLeastOneError
  }

  validate(form: TContactForm): TContactForm {
    const newForm: TContactForm = { ...form }

    // NOTE FOR MYSELF: If captcha V2 is present, I may need to delete captcha V3 to avoid having both
    if (!this.isValidCaptcha(form.captchaV2.value)) {
      delete newForm.captchaV2.error

      if (!this.isValidCaptcha(form.captchaV3.value)) {
        newForm.captchaV3.error = this.translator('captcha.required')
      } else {
        delete newForm.captchaV3.error
      }
    } else {
      delete newForm.captchaV2.error
      delete newForm.captchaV3.error
    }

    if (!this.isValidEmail(form.email.value)) {
      newForm.email.error = this.translator('contact.email.invalid')
    } else {
      delete newForm.email.error
    }

    if (this.isEmptyFirstname(form.firstname.value)) {
      newForm.firstname.error = this.translator('contact.firstname.invalid')
    } else if (this.isTooLongFirstname(form.firstname.value)) {
      newForm.firstname.error = this.translator('contact.firstname.tooLong')
    } else {
      delete newForm.firstname.error
    }

    if (!this.isValidLastname(form.lastname.value)) {
      newForm.lastname.error = this.translator('contact.lastname.invalid')
    } else {
      delete newForm.lastname.error
    }

    if (!this.isValidSubject(form.subject.value)) {
      newForm.subject.error = this.translator('contact.subject.invalid')
    } else {
      delete newForm.subject.error
    }

    if (this.isEmptyMessage(form.message.value)) {
      newForm.message.error = this.translator('contact.message.invalid')
    } else if (this.isTooShortMessage(form.message.value)) {
      newForm.message.error = this.translator('contact.message.tooShort')
    } else {
      delete newForm.message.error
    }

    return newForm
  }
}

export type TContactForm = {
  [key in 'email' | 'firstname' | 'lastname' | 'subject' | 'message' | 'captchaV3' | 'captchaV2']: {
    value: string
    error?: string
  }
}
